// =============================================
// useWorkerConfig — global key-value worker settings (NOT per-user)
// =============================================
// Reads/writes worker_config table. Used by:
//   - PipelineConfigPanel (Worker concurrency row: value/min/max)
//   - Settings (useWorkerMode toggle)
//   - Worker container (reads concurrency at startup, no hook — direct SQL)

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface ConcurrencyConfig {
  value: number;
  min: number;
  max: number;
}

interface WorkerConfigState {
  concurrency: ConcurrencyConfig;
  useWorkerMode: boolean;
  useWorkerModeMap: boolean;
  loading: boolean;
}

const DEFAULT_CONCURRENCY: ConcurrencyConfig = { value: 5, min: 1, max: 15 };

export function useWorkerConfig() {
  const [state, setState] = useState<WorkerConfigState>({
    concurrency: DEFAULT_CONCURRENCY,
    useWorkerMode: false,
    useWorkerModeMap: false,
    loading: true,
  });
  const { toast } = useToast();

  const fetchAll = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    const { data, error } = await (supabase as any)
      .from("worker_config")
      .select("key, value");

    if (error) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }

    const rows = (data || []) as Array<{ key: string; value: any }>;
    const concurrencyRow = rows.find((r) => r.key === "concurrency");
    const useWorkerModeRow = rows.find((r) => r.key === "use_worker_mode");
    const useWorkerModeMapRow = rows.find((r) => r.key === "use_worker_mode_map");

    setState({
      concurrency: concurrencyRow?.value
        ? {
            value: Number(concurrencyRow.value.value ?? DEFAULT_CONCURRENCY.value),
            min: Number(concurrencyRow.value.min ?? DEFAULT_CONCURRENCY.min),
            max: Number(concurrencyRow.value.max ?? DEFAULT_CONCURRENCY.max),
          }
        : DEFAULT_CONCURRENCY,
      useWorkerMode: Boolean(useWorkerModeRow?.value?.value ?? false),
      useWorkerModeMap: Boolean(useWorkerModeMapRow?.value?.value ?? false),
      loading: false,
    });
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const clamp = (v: number, min: number, max: number): number =>
    Math.max(min, Math.min(max, Math.floor(v)));

  const saveConcurrency = useCallback(
    async (newValue: number) => {
      const clamped = clamp(newValue, state.concurrency.min, state.concurrency.max);
      const nextValue: ConcurrencyConfig = { ...state.concurrency, value: clamped };

      const { error } = await (supabase as any)
        .from("worker_config")
        .update({ value: nextValue, updated_at: new Date().toISOString() })
        .eq("key", "concurrency");

      if (error) {
        toast({
          title: "Blad zapisu concurrency",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      setState((s) => ({ ...s, concurrency: nextValue }));
      toast({
        title: "Zapisano concurrency",
        description: "Aby zmiana weszla, kliknij Redeploy w Coolify (Worker container).",
      });
    },
    [state.concurrency, toast],
  );

  const saveUseWorkerMode = useCallback(
    async (enabled: boolean) => {
      const { error } = await (supabase as any)
        .from("worker_config")
        .update({
          value: { value: enabled },
          updated_at: new Date().toISOString(),
        })
        .eq("key", "use_worker_mode");

      if (error) {
        toast({
          title: "Blad zapisu Worker Mode",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      setState((s) => ({ ...s, useWorkerMode: enabled }));
      toast({
        title: enabled ? "Worker Mode WLACZONY" : "Worker Mode WYLACZONY",
        description: enabled
          ? "Kolejki dziala na serwerze 24/7 (niezaleznie od karty przegladarki)."
          : "Powrot do browser mode — kolejka wymaga otwartej karty.",
      });
    },
    [toast],
  );

  const saveUseWorkerModeMap = useCallback(
    async (enabled: boolean) => {
      // Upsert — flag może być nowa (dopiero wprowadzona migracja 20260419)
      const { error } = await (supabase as any)
        .from("worker_config")
        .upsert({
          key: "use_worker_mode_map",
          value: { value: enabled, desc: "Seeds/nodes generation przez worker container zamiast browser" },
          updated_at: new Date().toISOString(),
        }, { onConflict: "key" });

      if (error) {
        toast({
          title: "Blad zapisu Worker Map Mode",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      setState((s) => ({ ...s, useWorkerModeMap: enabled }));
      toast({
        title: enabled ? "Worker Map Mode WLACZONY" : "Worker Map Mode WYLACZONY",
        description: enabled
          ? "Generowanie seedow/nodow leci przez worker. Mozesz zamknac karte w trakcie."
          : "Powrot do browser mode — generacja mapy wymaga otwartej karty.",
      });
    },
    [toast],
  );

  return {
    concurrency: state.concurrency,
    useWorkerMode: state.useWorkerMode,
    useWorkerModeMap: state.useWorkerModeMap,
    loading: state.loading,
    saveConcurrency,
    saveUseWorkerMode,
    saveUseWorkerModeMap,
    refresh: fetchAll,
  };
}

// Re-export Node-safe helpers (moved to @/lib/workerMode so worker can import without React deps)
export { isWorkerModeEnabled, isWorkerModeMapEnabled } from "@/lib/workerMode";
